import React from 'react';
import { Download, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { ApplicationsTable } from '../components/applications/ApplicationsTable';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { usePlatform } from '../contexts/PlatformContext';
import { entityName } from '../data/entities';
import { officers } from '../data/officers';
import { STAGE_META, MEGA_MAJOR_STAGES, INTERNATIONAL_STAGES } from '../utils/stages';
import type { StageId, Track } from '../types/domain';
const savedViews: {
  id: string;
  label: string;
  description: string;
}[] = [{
  id: 'all',
  label: 'All live records',
  description: 'Everything except declined applications'
}, {
  id: 'my-queue',
  label: 'My queue',
  description: 'Assigned to Hend AlMheiri'
}, {
  id: 'gates-open',
  label: 'Gates not cleared',
  description: 'Current-stage gate outstanding'
}, {
  id: 'sla-breach',
  label: 'SLA breached',
  description: 'Past the service standard'
}];
const allStages: StageId[] = Array.from(new Set([...MEGA_MAJOR_STAGES, ...INTERNATIONAL_STAGES]));
export function Applications() {
  const {
    applications
  } = usePlatform();
  const [track, setTrack] = React.useState<'all' | Track>('all');
  const [stage, setStage] = React.useState<'all' | StageId>('all');
  const [officer, setOfficer] = React.useState<'all' | string>('all');
  const [view, setView] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const records = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return applications.filter(record => {
      if (track !== 'all' && record.track !== track) return false;
      if (stage !== 'all' && record.stage !== stage) return false;
      if (officer !== 'all' && record.officer !== officer) return false;
      if (view === 'all' && record.status === 'rejected') return false;
      if (view === 'my-queue' && record.officer !== 'Hend AlMheiri') return false;
      if (view === 'sla-breach' && new Date(record.slaDueOn) >= new Date('2026-08-18')) return false;
      if (view === 'gates-open') {
        const items = record.gates[record.stage] ?? [];
        const cleared = items.length > 0 && items.every(item => item.status === 'met');
        if (cleared || items.length === 0) return false;
      }
      if (q && !record.eventName.toLowerCase().includes(q) && !record.ref.toLowerCase().includes(q) && !entityName(record.entityId).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [applications, track, stage, officer, view, query]);
  const counts = {
    all: applications.filter(r => r.status !== 'rejected').length,
    mega: applications.filter(r => r.track === 'mega').length,
    major: applications.filter(r => r.track === 'major').length,
    international: applications.filter(r => r.track === 'international').length
  };
  return <div data-id="element-981" data-mp="1gb7w14mswwaj">
      <PageHeader eyebrow="Pipeline" title="Applications queue" description="Every entity request for event support as a record. Stage, gate status and AI score travel with the record through its lifecycle." actions={<>
            <Button variant="outline" size="sm" onClick={() => toast.success(`${records.length} records queued for bulk assignment`, {
        description: 'Assignment is applied when an officer is selected.'
      })} data-id="element-983" data-mp="12djcq18dtx7l">
              <Users className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-984" data-mp="2mt4gk81saqsw" />
              Bulk assign
            </Button>
            <Button size="sm" onClick={() => toast.success('CSV export prepared', {
        description: `${records.length} records with stage, gate and score columns.`
      })} data-id="element-985" data-mp="2is2ivvnimmt1">
              <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-986" data-mp="3i54983h8lr8l" />
              Export CSV
            </Button>
          </>} data-id="element-982" data-mp="1v1yn2agkjrcl" />

      <div className="space-y-4 p-6" data-id="element-987" data-mp="3s2iinj4qtfre">
        <Tabs value={track} onValueChange={value => setTrack(value as 'all' | Track)} data-id="element-988" data-mp="3rze6nzl1dfq6">
          <TabsList data-id="element-989" data-mp="t39iej5cz44">
            <TabsTrigger value="all" data-id="element-990" data-mp="4wri90jim0qy">All tracks ({counts.all})</TabsTrigger>
            <TabsTrigger value="mega" data-id="element-991" data-mp="4uohpmkqkp7z">Mega ({counts.mega})</TabsTrigger>
            <TabsTrigger value="major" data-id="element-992" data-mp="9osq8a9nh5b6">Major ({counts.major})</TabsTrigger>
            <TabsTrigger value="international" data-id="element-993" data-mp="en42ernf3rwk">
              International ({counts.international})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-2" data-id="element-994" data-mp="jjudt1oa1zbn">
          <div className="relative min-w-[240px] flex-1" data-id="element-995" data-mp="oljtiumg3162">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-996" data-mp="292j8imal1hg4" />
            <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search reference, event or entity" className="pl-8" aria-label="Search the applications queue" data-id="element-997" data-mp="1z4myxyxamuk4" />
          </div>

          <Select value={view} onValueChange={setView} data-id="element-998" data-mp="2rv51myu3utx0">
            <SelectTrigger className="w-[190px]" aria-label="Saved view" data-id="element-999" data-mp="2mvikieb5otix">
              <SelectValue placeholder="Saved view" data-id="element-1000" data-mp="2i2phbuvc5t8y" />
            </SelectTrigger>
            <SelectContent data-id="element-1001" data-mp="2d4nwp5i9hlvd">
              {savedViews.map(saved => <SelectItem key={saved.id} value={saved.id} data-id="element-1002" data-mp="2d2bgaws5jjkf">
                  {saved.label}
                </SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={stage} onValueChange={value => setStage(value as 'all' | StageId)} data-id="element-1003" data-mp="35rqvlyqunym5">
            <SelectTrigger className="w-[170px]" aria-label="Filter by stage" data-id="element-1004" data-mp="13wb8p01z971m">
              <SelectValue placeholder="Stage" data-id="element-1005" data-mp="18tb3ufxagpdy" />
            </SelectTrigger>
            <SelectContent data-id="element-1006" data-mp="u683m1hl02mx">
              <SelectItem value="all" data-id="element-1007" data-mp="u4wzg0k84sva">All stages</SelectItem>
              {allStages.map(stageId => <SelectItem key={stageId} value={stageId} data-id="element-1008" data-mp="z38d6mdn3nis">
                  {STAGE_META[stageId].label}
                </SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={officer} onValueChange={setOfficer} data-id="element-1009" data-mp="p86mhm3pude3">
            <SelectTrigger className="w-[190px]" aria-label="Filter by officer" data-id="element-1010" data-mp="b7dtx6wz5sdm">
              <SelectValue placeholder="Officer" data-id="element-1011" data-mp="g5p119vvxos5" />
            </SelectTrigger>
            <SelectContent data-id="element-1012" data-mp="g7s0slmuawa2">
              <SelectItem value="all" data-id="element-1013" data-mp="25kdcn8wnj2p4">All officers</SelectItem>
              {officers.map(person => <SelectItem key={person.id} value={person.name} data-id="element-1014" data-mp="2akhryvltprdr">
                  {person.name}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <p className="text-[12px] text-muted-foreground" data-id="element-1015" data-mp="3q3p6v5z5xf8j">
          {records.length} record{records.length === 1 ? '' : 's'} ·{' '}
          {savedViews.find(saved => saved.id === view)?.description}
        </p>

        <ApplicationsTable records={records} data-id="element-1016" data-mp="3uo8hr6jymmz" />
      </div>
    </div>;
}